import React, { useRef, useState } from 'react'
import { useQuery } from 'react-query'
import { useLocation } from 'react-router-dom'

import AdasReports from './AdasReports'
import UploadModal from './UploadModal'
import { ContainerProvider } from '../../containerContext'
import { useApi } from '../../../hooks'
import useAppContext from '../../app'
import { isReportProcessing } from './reportStatus'

const queryOrigin = {
  query: {
    page: 1,
    limit: 50,
    filter: {
      status: 'all',
      search: '',
    },
  },
  sorted: {
    sortBy: 'dateCreated',
    sortDirection: 'desc',
  },
}

const Index = () => {
  const [state, setState] = useState({ ...queryOrigin })
  const { adasReportService } = useApi()
  const { store: { clientSettings } } = useAppContext()
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const [showUpload, setShowUpload] = useState(params.get('upload') === 'true')
  // Tracks when we first observed a still-processing report so polling can be
  // time-boxed (a background parse failure can leave a report in subStatus
  // 'new' — we must not poll forever).
  const pollStartRef = useRef(null)

  const allowUpload = clientSettings && clientSettings.vehicleReports && clientSettings.vehicleReports.allowUpload
  // Company entitlement (active subscription or gift credits) — also gates the
  // modal so the ?upload=true URL param can't bypass the disabled button.
  const hasAdasCredits = clientSettings && clientSettings.vehicleReports && clientSettings.vehicleReports.hasAdasCredits

  const {
    isLoading,
    data = {},
    refetch,
  } = useQuery(
    ['adasReports', state.query, state.sorted],
    () => {
      const { filter, ...otherQuery } = state.query
      const params = {
        ...otherQuery,
        sortBy: state.sorted.sortBy,
        sortOrder: state.sorted.sortDirection === 'desc' ? -1 : 1,
      }

      if (filter.status && filter.status !== 'all') params.status = filter.status
      if (filter.search) params.search = filter.search
      if (filter.vendor && filter.vendor !== 'all') params.vendor = filter.vendor
      if (filter.insuranceCarrier && filter.insuranceCarrier !== 'all') params.insuranceCarrier = filter.insuranceCarrier
      if (filter.source && filter.source !== 'all') params.source = filter.source
      if (filter.startDate) params.dateFrom = filter.startDate
      if (filter.endDate) params.dateTo = filter.endDate

      return adasReportService.many(params)
    },
    {
      refetchOnWindowFocus: false,
      // A freshly uploaded report parses in the background (see main-API
      // POST /portal/vehicle-reports/upload). Poll while any report is still
      // processing so it resolves on its own without a manual refresh, but
      // time-box the polling to ~4 minutes so a stuck/failed report can't
      // trigger indefinite refetches.
      //
      // 240s, not 120s (AB#37798): the legacy adasThink platform is
      // synchronous and allows itself up to ~180s per report; the API's
      // transport timeout is 200s. This cap must sit ABOVE the server's, or
      // the placeholder gives up at the exact moment the outcome is being
      // written. The server fails anything stuck past 300s, so nothing can
      // shimmer forever. Ladder: vendor 180 < API 200 < THIS 240 < sweep 300.
      // The admin portal carries the same number in its interval poll — the
      // two portals deploy separately; change BOTH or neither.
      refetchInterval: (latest) => {
        const reports = (latest && latest.vehicleReports) || []
        // Poll while any client upload is still being enriched by adasThink.
        // A completed OR failed report is no longer processing, so polling stops.
        const stillProcessing = reports.some(isReportProcessing)
        if (!stillProcessing) {
          pollStartRef.current = null
          return false
        }
        if (!pollStartRef.current) pollStartRef.current = Date.now()
        if (Date.now() - pollStartRef.current > 240000) return false
        return 4000
      },
    },
  )

  return (
    <ContainerProvider
      state={state}
      setState={setState}
      data={data.vehicleReports || []}
      stateOrigin={queryOrigin}
      isLoading={isLoading}
      total={data.total || 0}
      pricingByVendor={data.pricingByVendor || {}}
      onUpload={() => setShowUpload(true)}
    >
      <AdasReports onUpload={() => setShowUpload(true)} />
      {allowUpload && hasAdasCredits && (
        <UploadModal
          show={showUpload}
          onHide={() => setShowUpload(false)}
          onSuccess={refetch}
        />
      )}
    </ContainerProvider>
  )
}

export default Index
